import { MenuItem, Popover, Stack } from '@mui/material';
import {
  MarketingAttributionModelTypes,
  MarketingAttributionMultiTouchModel,
} from '@server/marketing-attribution';
import SweepSelect, { SelectSizes } from '../../common/SweepSelect';
import { Button, Typography, colors } from '@sweep-io/sweep-design';
import usePopover from '../../../hooks/usePopover';
import { useMarketingAttributionModels } from '../../pages/canvas-pages/canvas-hubspot-page/marketing-attribution/useMarketingAttributionModels';
import { useMemo, useState } from 'react';
import { useRunOnce } from '../../common/useRunOnce';
import { ConditionsWrapper } from '../ConditionsWrapper';
import { AutomationFormVariant } from '../../../types/enums/AutomationFormVariant';
import { Weights } from './Weights';
import {
  getMarketingAttributionObjectList,
  getMarketingAttributionAction,
  getMarketingAttributionModelType,
  getStageId,
  getMarketingAttributionWeights,
  getMarketingAttributionFilter,
  MARKETING_ATTRIBUTION_FILTERS,
  MARKETING_ATTRIBUTION_DISPLAY_NAMES,
  subtitle,
  translateModelType,
} from './helpers';
import { getReadOnlyWeights } from '../../pages/canvas-pages/canvas-hubspot-page/marketing-attribution/utils';
import { fieldUpdate } from '../../pages/canvas-pages/canvas-hubspot-page/marketing-attribution/getAttributionModelsTemplates';
import { SearchInput } from '../../common/SearchInput';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { SFDCObjectType } from '../../../types/enums/SFDCObjectType';
import { MarketingAttributionModelType } from '../../../types/enums/MarketingAttributionModelType';

interface MarketingAttributionFormProps {
  onChange: (data: DeepPartial<AutomationStructureNew>) => void;
  readonly: boolean;
  model: DeepPartial<AutomationStructureNew>;
  setDisplayObject: (object: string) => void;
  removeConditionsToTrigger: () => void;
  removeMarketingAttributionConditions: (filterTypeToRemove: string) => void;
  helperShowFieldContextMenu: (
    automationVariant: AutomationFormVariant,
    automationJson: DeepPartial<AutomationStructureNew>,
  ) => boolean | undefined;
  setAutomationJson: (data: DeepPartial<AutomationStructureNew>) => void;
  crmOrgId: string;
  automationVariant: AutomationFormVariant;
}

export const MarketingAttributionForm = ({
  onChange,
  readonly,
  model,
  setDisplayObject,
  removeConditionsToTrigger,
  removeMarketingAttributionConditions,
  helperShowFieldContextMenu,
  setAutomationJson,
  crmOrgId,
  automationVariant,
}: MarketingAttributionFormProps) => {
  const { attributionFilters: NewAttributionFilters } = useFeatureToggle();
  const [opportunityStageStages, setOpportunityStageStages] = useState<
    {
      _stageId: string;
      stageName: string;
    }[]
  >([]);
  const [search, setSearch] = useState('');
  const { isPopoverOpen, closePopover, openPopover, anchorElement, anchorRef } = usePopover();
  const { getOpportunityStageStages } = useMarketingAttributionModels();
  const stage = getStageId(model);

  const modelType = getMarketingAttributionModelType(model);
  const action = getMarketingAttributionAction(model);
  const campaignCriteria = getMarketingAttributionFilter(
    model,
    MARKETING_ATTRIBUTION_FILTERS.CAMPAIGN,
  );
  const campaignMemberCriteria = getMarketingAttributionFilter(
    model,
    MARKETING_ATTRIBUTION_FILTERS.CAMPAIGN_MEMBER,
  );
  const opportunityContactRoleCriteria = getMarketingAttributionFilter(
    model,
    MARKETING_ATTRIBUTION_FILTERS.OPPORTUNITY_CONTACT_ROLE,
  );
  const weights = getMarketingAttributionWeights(model);
  const defaultWeights = getReadOnlyWeights(modelType);

  const marketingAttributionObjectList = useMemo(
    () => (model ? getMarketingAttributionObjectList(model, NewAttributionFilters) : []),
    [model, NewAttributionFilters],
  );

  useRunOnce(() => {
    getOpportunityStageStages().then(setOpportunityStageStages);
  });

  const filteredStages = useMemo(() => {
    return opportunityStageStages.filter((s) =>
      s.stageName.toLowerCase().includes(search.toLowerCase()),
    );
  }, [opportunityStageStages, search]);

  const createFilterConditionWrapper = (
    filterType: string,
    objectType: string,
    displayName: string,
    criteria: (SweepCondition & { isCriteria: boolean }) | undefined,
  ) => {
    const filterResult = getMarketingAttributionFilter(model, filterType);
    const isCriteria =
      filterResult && typeof filterResult === 'object' && 'isCriteria' in filterResult
        ? filterResult.isCriteria
        : false;

    if (!stage || !isCriteria) return null;

    return (
      <ConditionsWrapper
        removeConditionsToTrigger={() => removeMarketingAttributionConditions(filterType)}
        customHeader={`Apply when these conditions from the ${displayName} object are met:`}
        ruleBuilderProps={{
          readOnly: readonly,
          crmOrgId: crmOrgId ?? '',
          objectType: objectType,
          sweepCondition: {
            criteria: (criteria?.criteria as any) ?? [],
            criteriaLogic: criteria?.criteriaLogic ?? '',
          },
          onChange: (newSweepCondition) => {
            const _criteria = criteria || {
              criteria: [],
              criteriaLogic: '',
              isCriteria: true,
            };
            const action = getMarketingAttributionAction(model);
            const temp: any = { ..._criteria, ...newSweepCondition };
            setAutomationJson({
              ...model,
              automationDetails: {
                ...model.automationDetails,
                actions: [
                  {
                    ...action,
                    actionParams: {
                      ...action?.actionParams,
                      [filterType]: temp,
                    },
                  },
                ],
              },
            });
          },
          showFieldContextMenu: helperShowFieldContextMenu(automationVariant, model),
          referenceObjectType: SFDCObjectType.Opportunity,
        }}
      />
    );
  };

  const filterConfigs = [
    {
      filterType: MARKETING_ATTRIBUTION_FILTERS.CAMPAIGN,
      objectType: 'Campaign',
      displayName: MARKETING_ATTRIBUTION_DISPLAY_NAMES.CAMPAIGN,
      criteria: campaignCriteria,
    },
    {
      filterType: MARKETING_ATTRIBUTION_FILTERS.CAMPAIGN_MEMBER,
      objectType: 'CampaignMember',
      displayName: MARKETING_ATTRIBUTION_DISPLAY_NAMES.CAMPAIGN_MEMBER,
      criteria: campaignMemberCriteria,
    },
    {
      filterType: MARKETING_ATTRIBUTION_FILTERS.OPPORTUNITY_CONTACT_ROLE,
      objectType: 'OpportunityContactRole',
      displayName: MARKETING_ATTRIBUTION_DISPLAY_NAMES.OPPORTUNITY_CONTACT_ROLE,
      criteria: opportunityContactRoleCriteria,
    },
  ];

  return (
    <Stack height="100%" gap={1} pt={1.5}>
      <Typography variant="h4">Model Type</Typography>
      <SweepSelect
        FormControlProps={{
          sx: { width: '284px', mb: 1 },
        }}
        variant={SelectSizes.medium}
        SelectProps={{
          disabled: readonly,
          placeholder: 'Select the attribution model',
          value: modelType ?? '',
          renderValue: (value) => {
            return <Typography variant="body">{translateModelType(value as string)}</Typography>;
          },
          onChange: (event) => {
            const newModel = event.target.value as MarketingAttributionModelType;
            const isNewModelMultiTouch = newModel == 'MULTI_TOUCH';

            onChange({
              ...model,
              automationDetails: {
                ...model.automationDetails,
                automationParams: {
                  ...model.automationDetails?.automationParams,
                  fieldUpdate: {
                    ...fieldUpdate,
                    operatorValue: '',
                  },
                },
                actions: [
                  {
                    ...action,
                    actionParams: {
                      ...action?.actionParams,
                      modelType: newModel,
                      campaignFilter: {
                        criteria: [],
                        criteriaLogic: '',
                        isCriteria: false,
                      },
                      //only multi touch model has weights remembered in DB
                      weights: isNewModelMultiTouch ? getReadOnlyWeights(newModel) : undefined,
                    },
                  },
                ],
                when: {
                  criteria: {
                    criteria: [],
                    criteriaLogic: '',
                  },
                  isCriteria: false,
                },
              },
            });
          },
        }}
      >
        {Object.values(MarketingAttributionModelTypes).map((modelType) => (
          <MenuItem key={modelType} value={modelType}>
            <Stack width="280px">
              <Typography variant="body">{translateModelType(modelType)}</Typography>
              <Typography variant="caption" color={colors.grey[700]} whiteSpace="break-spaces">
                {subtitle(modelType)}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </SweepSelect>

      {modelType && (
        <>
          <Stack height="100%" gap={1} pt={2} pb={1}>
            <Typography variant="h4">Trigger attribution</Typography>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="body" color={colors.grey[900]}>
                When opportunity reaches stage:
              </Typography>

              <SweepSelect
                variant={SelectSizes.medium}
                SelectProps={{
                  onOpen: () => {
                    setSearch('');
                  },
                  onKeyDownCapture: (e) => {
                    //prevent the default behavior of the input to search for stages on keydown
                    e.stopPropagation();
                  },
                  disabled: readonly,
                  placeholder: 'Choose stage',
                  renderValue: (value) => {
                    // to prevent clearing the input when the stage of value is not in the filtered list
                    const stage = opportunityStageStages.find((s) => s._stageId === value);
                    return stage?.stageName ?? 'Choose stage';
                  },
                  value: stage ?? '',
                  onChange: (event) => {
                    const val = event.target.value;
                    const stage = opportunityStageStages.find((s) => s._stageId === val);

                    if (!stage) return;

                    onChange({
                      ...model,
                      automationDetails: {
                        ...model.automationDetails,
                        automationParams: {
                          ...model.automationDetails?.automationParams,
                          fieldUpdate: {
                            ...fieldUpdate,
                            operatorValue: val,
                          },
                        },
                      },
                    });
                  },
                }}
              >
                <Stack
                  sx={{
                    position: 'sticky',
                    minWidth: '250px',
                    top: 0,
                    zIndex: 1000,
                    p: 1,
                    pt: 1,
                    mt: -1,
                    background: colors.white,
                  }}
                >
                  <SearchInput
                    value={search}
                    autoFocus
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                    placeholder="Search stage"
                  />
                </Stack>
                {filteredStages?.length === 0 && (
                  <Stack p={1} pl={1.5} justifyContent="center" height="100%">
                    <Typography variant="body" color={colors.grey[700]}>
                      No stages found
                    </Typography>
                  </Stack>
                )}
                {filteredStages.map((s) => (
                  <MenuItem key={s._stageId} value={s._stageId}>
                    {s.stageName}
                  </MenuItem>
                ))}
              </SweepSelect>
            </Stack>
          </Stack>

          {stage && marketingAttributionObjectList.length > 0 && (
            <Stack direction="column" gap={1} pt={2} pb={1}>
              <Typography variant="h4">Conditions (optional)</Typography>
              <Typography variant="body" color={colors.grey[900]}>
                Add conditions to include only specific records
              </Typography>
              <Button
                variant="flat-blue"
                ref={anchorRef}
                startIconName="Plus"
                onClick={() => openPopover()}
              >
                Add conditions
              </Button>

              <Popover
                open={isPopoverOpen}
                onClose={closePopover}
                anchorEl={anchorElement}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Stack p={1}>
                  <MenuItem
                    disabled
                    sx={{
                      '&.Mui-disabled': { opacity: 1 },
                      marginBottom: 0.5,
                    }}
                  >
                    <Typography variant="body-bold">Add conditions:</Typography>
                  </MenuItem>

                  {marketingAttributionObjectList.map((object) => (
                    <MenuItem
                      onClick={() => {
                        setDisplayObject(object);
                        closePopover();
                      }}
                      key={object}
                      value={object}
                      sx={{ padding: '8px 12px', width: '295px' }}
                    >
                      {object}
                    </MenuItem>
                  ))}
                </Stack>
              </Popover>
            </Stack>
          )}
        </>
      )}

      {stage && model.automationDetails?.when?.isCriteria && (
        <ConditionsWrapper
          removeConditionsToTrigger={removeConditionsToTrigger}
          customHeader={'Apply when these conditions from the Opportunity object are met:'}
          ruleBuilderProps={{
            readOnly: readonly,
            crmOrgId: crmOrgId ?? '',
            objectType: 'Opportunity',
            sweepCondition: model.automationDetails?.when?.criteria,
            onChange: (newSweepCondition) => {
              const _criteria = model.automationDetails?.when?.criteria || {
                criteria: [],
                criteriaLogic: '',
              };
              const temp: CriteriaDetails = {
                ...model.automationDetails?.when,
                criteria: { ..._criteria, ...newSweepCondition },
              };
              setAutomationJson({
                ...model,
                automationDetails: {
                  ...model.automationDetails,
                  when: temp,
                },
              });
            },
            showFieldContextMenu: helperShowFieldContextMenu(automationVariant, model),
          }}
        />
      )}

      {stage && (
        <>
          {filterConfigs.map((config) =>
            createFilterConditionWrapper(
              config.filterType,
              config.objectType,
              config.displayName,
              config.criteria,
            ),
          )}
        </>
      )}

      {stage && (
        <Weights
          modelType={getMarketingAttributionModelType(model)}
          readonly={readonly || modelType !== 'MULTI_TOUCH'}
          weights={weights ?? defaultWeights}
          onChange={(weights) => {
            const action = getMarketingAttributionAction(model);

            if (action && action.actionParams) {
              const actionParams = action.actionParams as MarketingAttributionMultiTouchModel;

              setAutomationJson({
                ...model,
                automationDetails: {
                  ...model.automationDetails,
                  actions: [
                    {
                      ...action,
                      actionParams: {
                        ...actionParams,
                        weights,
                      },
                    },
                  ],
                },
              });
            }
          }}
        />
      )}
    </Stack>
  );
};
